// Utils
import { stringify } from "qs";
import { useEffect } from "react";
import { useHistory } from "react-router";
import {
    FILTER_ALERT,
    type FilterAlertTypeEnum,
    FROM,
    TO,
} from "src/components/Filters/FilterAlert/hooks/useFilterAlert.hooks";
import { useQueryString } from "src/design-system/core/hooks";
import { useRouteMetadata, useStorageQueryString } from "src/hooks";
import { PATH_OFFER } from "src/routesConstants";
import { PageEnum } from "src/utils/tracking/types/pageEnum";

export const OFFER_LIST_VIEW_TAB_QS_KEY = "offer-list-view-tab";
export const OFFER_LIST_OFFER_ID_QS_KEY = "offer-ids";
export const OFFER_LIST_FROM_MARKETING_FEEDBACK_QS_KEY = "from-marketing-feedback";
export const OFFER_LIST_GROUP_BY_QS_KEY = "offer-group-by";
export const OFFER_POS_QS_KEY = "offer-pos";
export const OFFER_CHANNEL_QS_KEY = "offer-channel";
export const OFFER_KPI_KEY_QS_KEY = "kpi-key";

export enum OffersTablesTabsEnum {
    DASHBOARD = "dashboard",
    MANAGEMENT = "management",
    PERFORMANCE = "performance",
}

const offersTabToPage: Record<OffersTablesTabsEnum, PageEnum> = {
    [OffersTablesTabsEnum.MANAGEMENT]: PageEnum.OfferManagement,
    [OffersTablesTabsEnum.DASHBOARD]: PageEnum.OfferDashboard,
    [OffersTablesTabsEnum.PERFORMANCE]: PageEnum.OfferDetail,
};

interface OffersBasicParams {
    tab?: OffersTablesTabsEnum;
    offerIds?: number[];
    groupBy?: string;
    filterAlert?: FilterAlertTypeEnum;
    from?: string;
    to?: string;
    kpiKey?: string;
    fromMarketingFeedback?: boolean;
}

interface OffersExtendedParams {
    tab?: OffersTablesTabsEnum;
    offerIds?: number[];
    groupBy?: string;
    filterAlert?: FilterAlertTypeEnum;
    baseKey?: string;
    compareKey?: string;
    pos?: number[];
    channel?: number[];
    kpiKey?: string;
    fromMarketingFeedback?: boolean;
}

export const offersRoutes = {
    createView: ({ offerId, duplicate }: { offerId?: number; duplicate?: number } = {}): string =>
        `${PATH_OFFER}/new${stringify(
            {
                offerId,
                duplicate,
            },
            { addQueryPrefix: true }
        )}`,
    tablesView: (params: OffersBasicParams | OffersExtendedParams = {}): string => {
        const { tab, offerIds, groupBy, filterAlert, kpiKey, fromMarketingFeedback } = params;

        const queryParams: Record<string, unknown> = {
            [OFFER_LIST_VIEW_TAB_QS_KEY]: tab,
            [OFFER_LIST_OFFER_ID_QS_KEY]: offerIds,
            [OFFER_LIST_GROUP_BY_QS_KEY]: groupBy,
            [FILTER_ALERT]: filterAlert,
            [OFFER_KPI_KEY_QS_KEY]: kpiKey,
            [OFFER_LIST_FROM_MARKETING_FEEDBACK_QS_KEY]:
                fromMarketingFeedback === true ? "1" : undefined,
        };

        if ("from" in params) {
            queryParams[FROM] = params.from;
            queryParams[TO] = params.to;
        }

        if ("baseKey" in params) {
            queryParams.baseKey = params.baseKey;
            queryParams.compareKey = params.compareKey;
            queryParams[OFFER_POS_QS_KEY] = params.pos;
            queryParams[OFFER_CHANNEL_QS_KEY] = params.channel;
        }

        return `${PATH_OFFER}${stringify(queryParams, { addQueryPrefix: true })}`;
    },
};

export function useOffersTableViewTabs(defaultTab: OffersTablesTabsEnum): {
    offersTableTab: OffersTablesTabsEnum;
    setOffersTableTab: (offersTableTab: OffersTablesTabsEnum) => void;
} {
    const history = useHistory();
    const [offersTableTab] = useQueryString(OFFER_LIST_VIEW_TAB_QS_KEY, defaultTab);
    const { updateMetadata } = useRouteMetadata();
    const setOffersTableTab = (offersTableTab: OffersTablesTabsEnum) =>
        history.push(offersRoutes.tablesView({ tab: offersTableTab }));

    useEffect(() => {
        const resolvedTab = offersTableTab ?? defaultTab;
        updateMetadata({
            page: offersTabToPage[resolvedTab] ?? PageEnum.OfferManagement,
        });
    }, [defaultTab, offersTableTab, updateMetadata]);

    return {
        offersTableTab: offersTableTab ?? defaultTab,
        setOffersTableTab,
    };
}

export const DUO_EXTENDED_METRICS_QS_KEY = "duo-extended-metrics";
export function useExtendedRouterMetrics(): { hasDuoExtendedMetrics: boolean } {
    const hasDuoExtendedMetrics = useStorageQueryString(DUO_EXTENDED_METRICS_QS_KEY);

    return { hasDuoExtendedMetrics };
}
